import { AlertPosition, IAlertProps } from "@components/alert/Alert.types";
import { Button, ButtonGroup } from "@components/button";
import { WithConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import { IEntity } from "@odata/BindingContext";
import {
    IPrCareReceiverSocialEventEntity,
    IPrPaternityLeaveEventEntity,
    IPrSocialEventEntity,
    PrCaregivingEventEntity,
    PrCareReceiverEntity,
    PrPaternityLeaveEventEntity,
    PrSocialEventEntity
} from "@odata/GeneratedEntityTypes";
import { PrSocialEventTypeCode, PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import {
    getEmployeeForCurrentUser,
    handleIdentificationTypeBeforeSave,
    IdentificationTypeLocalPath,
    initIdentificationTypeLocalPath,
    loadEmployments
} from "@pages/payroll/employee/Employee.utils";
import {
    addEmptyChildLineItem,
    canChangeEmployee,
    careReceiverBeforeSave,
    ISocialEventsFormCustomData
} from "@pages/payroll/socialEvents/SocialEvents.utils";
import { getNempriSubmissionSubTypeFromSocialEventType } from "@pages/payroll/submission/Submission.utils";
import SubmissionWizard from "@pages/payroll/submission/submissionWizard/SubmissionWizard";
import { cloneDeep } from "lodash";
import React from "react";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { WithPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { FormMode } from "../../../enums";
import { EmptyObject } from "../../../global.types";
import { getUtcDayjs } from "../../../types/Date";
import { IProps as IFormProps } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import TemporalFormDialog from "../../../views/formView/TemporalFormDialog";
import { loadAndStoreRanges, setMatchingNumberRange } from "../../numberRange/NumberRange.utils";

interface IProps<E extends IPrSocialEventEntity> extends IFormViewProps<E, ISocialEventsFormCustomData>, WithConfirmationDialog, WithPermissionContext {
}

abstract class SocialEventsFormView<E extends IPrSocialEventEntity> extends FormViewForExtend<E, IProps<E>> {
    static contextType = AppContext;

    constructor(props: IProps<E>) {
        super(props);

        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.onBeforeSave = this.onBeforeSave.bind(this);
        this.handleLineItemsChange = this.handleLineItemsChange.bind(this);
        this.onAfterLoad = this.onAfterLoad.bind(this);
        this.renderSocialEventButtons = this.renderSocialEventButtons.bind(this);
    }

    get hideBreadcrumbsBack(): boolean {
        return true;
    }

    componentDidMount(): void {
        super.componentDidMount();
        this.registerHeader();
    }

    componentDidUpdate(prevProps: Readonly<IProps<E>>, prevState: Readonly<EmptyObject>): void {
        super.componentDidUpdate(prevProps, prevState);
        this.registerHeader();
    }

    get hasCareReceiver(): boolean {
        return ![PrSocialEventTypeCode.IllnessLeave, PrSocialEventTypeCode.ParentalLeave, PrSocialEventTypeCode.Custody, PrSocialEventTypeCode.Imprisonment].includes(this.typeCode);
    }

    get hasCareReceiverCollection(): boolean {
        return [PrSocialEventTypeCode.MaternityLeave, PrSocialEventTypeCode.PaternityLeave].includes(this.typeCode);
    }

    abstract typeCode: PrSocialEventTypeCode;

    async onAfterLoad(): Promise<void> {
        const { storage } = this.props;

        storage.setCustomData({ type: this.typeCode });
        const isAuditTrail = storage.formMode === FormMode.AuditTrail;

        if (!isAuditTrail) {
            const promises: Promise<unknown>[] = [];

            const isNew = storage.data.bindingContext?.isNew();

            if (!storage.data.entity.NumberOurs) {
                promises.push(setMatchingNumberRange(storage, true));
            } else {
                promises.push(loadAndStoreRanges(storage));
            }

            const _canChangeEmployee = canChangeEmployee({ storage });
            if (isNew) {
                storage.setValueByPath(PrSocialEventEntity.SocialEventType, this.typeCode);

                // set default Employee and Employment
                if (!_canChangeEmployee) {
                    promises.push(this.initEmployee());
                }
            } else {
                if (_canChangeEmployee) {
                    // we need to fetch Employments to know if to show Employment field
                    // (it's visible only for 2+ employments)
                    promises.push(this.loadEmployments());
                }
            }

            if (this.hasCareReceiver) {
                if (this.hasCareReceiverCollection) {
                    if (isNew) {
                        // insert first empty care receiver line item
                        addEmptyChildLineItem(storage as unknown as FormStorage);
                    }

                    // care receiver collection
                    const receivers = storage.getValueByPath(PrPaternityLeaveEventEntity.CareReceivers) as IPrCareReceiverSocialEventEntity[];
                    const basePath = storage.data.bindingContext.navigate(PrPaternityLeaveEventEntity.CareReceivers);
                    receivers?.forEach((item, index) => {
                        const itemBc = basePath.addKey(item);
                        initIdentificationTypeLocalPath(storage, itemBc, PrCareReceiverEntity.IdentificationNumber);
                    });
                } else {
                    // single care receiver
                    initIdentificationTypeLocalPath(storage,
                        storage.data.bindingContext.navigate(PrCaregivingEventEntity.CareReceiver),
                        PrCareReceiverEntity.IdentificationNumber
                    );
                }
            }

            await Promise.all(promises);
        }

        return super.onAfterLoad();
    }

    shouldHideButtons(): boolean {
        // buttons should be visible even on customer portal
        //  todo: consider permissions...
        return false;
    }

    getFormProps = (): Partial<IFormProps> => {
        const { formProps } = this.props;
        const { isEndDialogOpened } = this.props.storage.getCustomData();
        return {
            ...formProps,
            hideBusyIndicator: formProps?.hideBusyIndicator || isEndDialogOpened,
            suppressAlert: isEndDialogOpened
        };
    };

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        switch (actionId) {
            case PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication:
            case PrSubmissionTypeCode.ReportingByTheEmployerUponTerminationOfIncapacityForWork:
                this.props.storage.setCustomData({ submissionType: actionId });
                this.forceUpdate();
                break;
            default:
                return super.handleCustomHeaderAction(actionId);
        }
    }

    initEmployee = async (): Promise<void> => {
        const { storage } = this.props;
        const employee = await getEmployeeForCurrentUser(storage);
        // todo: what if current user doesn't have employee entity?
        storage.setValueByPath(PrSocialEventEntity.Employee, employee);
        this.loadEmployments();
    };

    async loadEmployments(): Promise<void> {
        const storage = this.props.storage as unknown as FormStorage;
        return loadEmployments(storage, storage.data.bindingContext.navigate(PrSocialEventEntity.Employment), this.handleChange);
    }

    registerHeader = (): void => {
        const { storage } = this.props;
        if (storage.data.bindingContext) {
            const numberOursBc = storage.data.bindingContext.navigate(PrSocialEventEntity.NumberOurs);
            storage.addCustomRef(this._refHeader.current, numberOursBc);
        }
    };

    handleNumberRange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === PrSocialEventEntity.NumberRange) {
            this.props.storage.data.entity.NumberRange = e.value ? e.additionalData : null;
        }
    };

    handleEmployeeChange = async (e: ISmartFieldChange): Promise<void> => {
        if (e.bindingContext.getPath() === PrSocialEventEntity.Employee && e.triggerAdditionalTasks) {
            await this.loadEmployments();
            this.props.storage.refreshFields();
        }
    };

    async handleBlur(args: ISmartFieldBlur): Promise<void> {
        const { storage } = this.props;
        if (args.wasChanged) {

        }
        await storage.handleBlur(args);
        storage.refreshFields(true);
    }

    handleChange(e: ISmartFieldChange): void {
        const { storage } = this.props;
        storage.handleChange(e);
        this.handleNumberRange(e);
        this.handleEmployeeChange(e);
        storage.refreshFields(e.triggerAdditionalTasks);
    }

    onBeforeSave(): E {
        const { storage } = this.props;
        let entity: E = cloneDeep(storage.data.entity) as E;
        if (this.hasCareReceiver) {
            entity = careReceiverBeforeSave<E>(storage, entity);
            if (this.hasCareReceiverCollection) {
                const receivers = (entity as IPrPaternityLeaveEventEntity)[PrPaternityLeaveEventEntity.CareReceivers];
                const baseBc = storage.data.bindingContext.navigate(PrPaternityLeaveEventEntity.CareReceivers);
                receivers.forEach((item, index) => {
                    handleIdentificationTypeBeforeSave(entity, storage.data.bindingContext,
                        baseBc.addKey(item),
                        { identificationPropName: PrCareReceiverEntity.IdentificationNumber });
                });
            } else {
                handleIdentificationTypeBeforeSave(entity, storage.data.bindingContext,
                    storage.data.bindingContext.navigate(PrCaregivingEventEntity.CareReceiver),
                    { identificationPropName: PrCareReceiverEntity.IdentificationNumber });
            }
        }
        return entity;
    }

    async handleLineItemsChange(args: ISmartFieldChange): Promise<void> {
        if (args.bindingContext.getPath() === IdentificationTypeLocalPath) {
            this.props.storage.refresh(true);
        }
        super.handleLineItemsChange(args);
    }

    get submissionDefaultData(): IEntity {
        const { storage } = this.props;
        const { bindingContext: bc } = storage.data;
        return {
            SocialEvent: {
                Id: bc.getKey(),
                _metadata: {
                    "": { type: `#${bc.getEntityType().getFullName()}` }
                }
            }
        };
    }

    get submissionSubtype(): PrSubmissionSubtypeCode {
        return getNempriSubmissionSubTypeFromSocialEventType(this.typeCode);
    }

    /* Shared code between EmployeeFormView, SocialEventFormView and EmploymentFormView */
    closeSubmissionDialog = (): void => {
        this.props.storage.setCustomData({ submissionType: null });
        this.forceUpdate();
    };

    confirmSubmissionDialog = (): void => {
        const { storage } = this.props;
        // Submission saved -> reload the form
        // todo: reload only the table?
        storage.reload({ preserveInfos: true, preserveData: true, withoutBusy: true });
        // close the dialog
        this.closeSubmissionDialog();
    };

    renderSubmissionDialog = () => {
        const { storage } = this.props;
        const { submissionType } = storage.getCustomData();
        if (submissionType) {
            const isNEMPRI = submissionType === PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication;
            return (
                <SubmissionWizard generatorKey={storage.data.bindingContext?.getKey()}
                                  onCancel={this.closeSubmissionDialog}
                                  onConfirm={this.confirmSubmissionDialog}
                                  defaultData={this.submissionDefaultData}
                                  mainType={submissionType}
                                  subType={isNEMPRI ? this.submissionSubtype : null}/>
            );
        }
        return null;
    }
    /* /end/ Shared code between EmployeeFormView, SocialEventFormView and EmploymentFormView */

    scrollPageUp(): void {
        const { storage } = this.props;
        if (storage.getCustomData().isEndDialogOpened) {
            // do not scroll page up when end dialog is opened, we handle everything in the dialog
            return;
        }
        return super.scrollPageUp();
    }

    clearEndEventData(): void {
        const { storage } = this.props;
        storage.data.entity.DateEventEnd = null;
        storage.data.entity.WorkedHoursOnLastDay = null;
        storage.data.entity.MonthDetails = storage.data.entity.MonthDetails
            ?.filter(md => !md.IsEnd) ?? [];
    }

    handleEventEndToggle = async (): Promise<void> => {
        const { storage } = this.props;
        const { DateEventEnd } = storage.data.origEntity;

        if (DateEventEnd) {
            this.clearEndEventData();
            await this.save({
                successSubtitle: storage.t("SocialEvents:Form.ReopenEventSuccess"),
            });
        } else {
            if (storage.isDirty()) {
                // todo: ask user to save changes before opening the dialog ?? Handle validations
                await storage.reload({ preserveInfos: true, preserveData: false, withoutBusy: true });
            }
            storage.setCustomData({ isEndDialogOpened: true });
            storage.refresh(true);
        }
    };

    handleCloseEventEndDialog = async (isConfirmed: boolean): Promise<void> => {
        const { storage } = this.props;
        if (isConfirmed) {
            const result = await this.save({
                successSubtitle: storage.t("SocialEvents:Form.EndEventSuccess")
            });
            if (!result) {
                const { alert } = storage.data;
                const alertProps: IAlertProps = alert ? {
                    ...alert,
                    position: AlertPosition.Default,
                    isSmall: true,
                    isFullWidth: true,
                } : null;
                storage.setCustomData({ endDialogAlert: alertProps });
                storage.data.alert = null; // clear the alert shown in the dialog
                this.forceUpdate();
                return;
            }
        } else {
            this.clearEndEventData();
        }
        storage.setCustomData({ isEndDialogOpened: false, endDialogAlert: null });
        storage.refresh(true);
    };

    handleCloseDialogTemporalChange = (args: ISmartFieldChange): void => {
        if (args.bindingContext.getPath() === PrSocialEventEntity.DateEventEnd && args.triggerAdditionalTasks) {
            const socialEventEndDate = args.value as Date;
            const storage = this.props.storage;
            const socialEventStartDate = storage.getValueByPath(PrSocialEventEntity.DateEventStart) as Date;
            if (socialEventEndDate && socialEventStartDate && getUtcDayjs(socialEventEndDate).isSame(getUtcDayjs(socialEventStartDate), "day")) {
                storage.setTemporalData(storage.data.bindingContext.navigate(PrSocialEventEntity.WorkedHoursOnLastDay), { value: storage.getValueByPath(PrSocialEventEntity.WorkedHoursOnFirstDay) });
            }
        }
    };

    renderEventEndDialog(endDialogAlert: IAlertProps): React.ReactElement {
        return (
            <TemporalFormDialog storage={this.props.storage as unknown as FormStorage}
                                alert={endDialogAlert}
                                fieldPaths={[[PrSocialEventEntity.DateEventEnd, PrSocialEventEntity.WorkedHoursOnLastDay]]}
                                title={this.props.storage.t("SocialEvents:Form.EndEventDialogTitle")}
                                onTemporalChange={this.handleCloseDialogTemporalChange}
                                onClose={this.handleCloseEventEndDialog}/>
        );
    }

    renderCustomDialogs(): React.ReactElement {
        const { storage } = this.props;
        const { isEndDialogOpened, endDialogAlert } = storage.getCustomData();
        return (
            <>
                {super.renderCustomDialogs()}
                {this.renderSubmissionDialog()}
                {isEndDialogOpened ? this.renderEventEndDialog(endDialogAlert) : null}
            </>);
    }

    renderSocialEventButtons(additionalButton?: React.ReactElement, showEndButton = true): React.ReactElement {
        const storage = this.props.storage;
        const isFormReadOnly = storage.isReadOnly;
        const isNew = storage.data.bindingContext.isNew();

        const { DateEventEnd } = storage.data.origEntity ?? {};

        return (
            <ButtonGroup wrap={"wrap"}>
                {!isFormReadOnly && (
                    <Button hotspotId={"formCancel"}
                        isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                        onClick={this.handleFormCancel}
                        isTransparent>{storage.t("Common:General.Cancel")}</Button>
                )}
                {this.isDeletable &&
                    <SmartFormDeleteButton storage={storage}
                                           onClick={this.handleDelete}/>
                }
                {additionalButton}
                {!isNew && showEndButton && (
                    <Button hotspotId={"endEvent"}
                            onClick={this.handleEventEndToggle}
                            isTransparent>
                        {storage.t(DateEventEnd ? "SocialEvents:Form.ReopenEvent" : "SocialEvents:Form.EndEvent")}
                    </Button>
                )}
                {this.shouldAllowCopy() && <Button hotspotId={"formCopy"}
                                                   onClick={this.handleCopy}
                                                   isDisabled={this.shouldDisableCopyButton()}
                                                   isTransparent>{storage.t("Common:Form.CreateCopy")}</Button>}
                {!isFormReadOnly &&
                    <Button hotspotId={"formSave"}
                            onClick={this.handleSaveClick}
                            isDisabled={this.shouldDisableSaveButton()}>
                        {storage.t("Common:General.Save")}
                    </Button>
                }
            </ButtonGroup>
        );
    }
}

export { SocialEventsFormView as SocialEventsFormViewForExtend };